// ApiBase.jsx
import TokenService from "./tokenService";

//const BASE_URL = 'http://localhost:5206/api';
const BASE_URL = '/api';
/**
 * Executes an API request with proper token validation
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {string} endpoint - API endpoint
 * @param {string} token - JWT token
 * @param {Object} data - Request payload data
 * @returns {Promise<Object>} - Response with isSuccess, data, errorMsg and status
 *
 * `status` e' il codice HTTP della risposta (0 se la richiesta non e' nemmeno partita): serve a
 * chi deve distinguere un no previsto - per esempio il 409 di un'edizione avviata - da un errore.
 */
const request = async (method, endpoint, token, data = null) => {
  try {
    // Update last activity timestamp
    TokenService.updateActivity();

    // Il token di default e' quello in sessione: diverse chiamate in API.jsx passano null e
    // finivano per mandare "Bearer null". Finche' il backend non controllava niente non si
    // notava, ma con la FallbackPolicy quelle chiamate diventerebbero 401.
    const headers = { Authorization: `Bearer ${token ?? TokenService.getToken()}` };
    let options = { method, headers, credentials: "same-origin" };

    if (data) {
      if (method === "GET") {
        endpoint += "?" + new URLSearchParams(data).toString();
      } 
      else {
        headers["Content-Type"] = "application/json";
        options.body = JSON.stringify(data);
      }
    }

    const response = await fetch(endpoint, options);

    // If unauthorized (token expired), redirect to login
    if (response.status === 401) {
      TokenService.removeToken();
      //window.location.href = '/login';
      return { isSuccess: false, data: undefined, errorMsg: 'Session expired', status: 401 };
    }

    const contentType = response.headers.get("content-type");
    let respData;
    if (contentType && contentType.includes("application/json")) {
      respData = await response.json();
    }
    else if (contentType && contentType.includes("text/plain")) {
      respData = await response.text();
    }

    if (!response.ok) {
      const errorMsg = Array.isArray(respData) ? respData.join(" · ")
                     : typeof respData === "string" && respData ? respData
                     : `Errore ${response.status}`;
      return { isSuccess: false, data: respData, errorMsg, status: response.status };
    }

    return { isSuccess: true, data: respData, status: response.status };
  }
  catch (error) {
    console.error(`Error in ${method} request to ${endpoint}:`, error);
    return { isSuccess: false, data: undefined, errorMsg: error.message, status: 0 };
  }
};

/**
 * Generic API client factory for a specific resource
 * @param {string} resourceName - API resource path (e.g., 'person', 'team')
 * @returns {Object} - Object with CRUD methods for the resource
 */
const createApiClient = (resourceName) => {
  const resourceUrl = `${BASE_URL}/${resourceName}`;
  
  return {
    all: async () => {
      const token = TokenService.getToken();
      return request("GET", `${resourceUrl}/all`, token);
    },
    
    getById: async (id) => {
      const token = TokenService.getToken();
      return request("GET", `${resourceUrl}/${id}`, token);
    },
    
    create: async (data) => {
      const token = TokenService.getToken();
      return request("POST", resourceUrl, token, data);
    },
    
    update: async (data) => {
      const token = TokenService.getToken();
      return request("PUT", resourceUrl, token, data);
    },
    
    delete: async (data) => {
      const token = TokenService.getToken();
      return request("DELETE", resourceUrl, token, data);
    },
    
    // Allow for custom endpoint methods
    custom: async (endpoint, method, data) => {
      const token = TokenService.getToken();
      const url = `${resourceUrl}/${endpoint}`;
      return request(method, url, token, data);
    }
  };
};

const debugApiCall = async (method, endpoint, token, data = null) => {
  
  try {
    console.log('=== DEBUG API CALL ===');
    console.log('Token:', token ? 'Present' : 'Missing');
    console.log('Data to send:', JSON.stringify(data, null, 2));
    console.log('Data size:', JSON.stringify(data).length, 'characters');
    
    const response = await fetch(endpoint, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      credentials: 'same-origin',
      body: JSON.stringify(data)
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', [...response.headers.entries()]);
    
    const responseText = await response.text();
    console.log('Response body:', responseText);
    
    if (response.ok) {
      try {
        const jsonResponse = JSON.parse(responseText);
        return { isSuccess: true, data: jsonResponse };
      } catch (e) {
        return { isSuccess: true, data: responseText };
      }
    } else {
      return { 
        isSuccess: false, 
        errorMsg: `HTTP ${response.status}: ${responseText}` 
      };
    }
  } catch (error) {
    console.error('Fetch error:', error);
    return { 
      isSuccess: false, 
      errorMsg: error.message 
    };
  }
};

// Simple HTTP method helpers
export const get = (endpoint, token, data, debug = false) => debug ? debugApiCall("GET", endpoint, token, data) : request("GET", endpoint, token, data);
export const post = (endpoint, token, data, debug = false) => debug ? debugApiCall("POST", endpoint, token, data) : request("POST", endpoint, token, data);
export const put = (endpoint, token, data, debug = false) => debug ? debugApiCall("PUT", endpoint, token, data) : request("PUT", endpoint, token, data);
export const del = (endpoint, token, data, debug = false) => debug ? debugApiCall("DELETE", endpoint, token, data) : request("DELETE", endpoint, token, data);

// Export the factory function
export { createApiClient, BASE_URL };